import { create } from 'zustand';
import type { StoreApi, UseBoundStore } from 'zustand';

export type DrawerStackEntry = {
  key: string;
  itemId: string;
};

export type DrawerStore = {
  stack: DrawerStackEntry[];
  isOpen: boolean;
  open: (id: string) => void;
  close: () => void;
  /**
   * Clears this store's stack only. Before navigating out of a nested
   * drawer chain (e.g. contact → client → "Open full client"), call
   * {@link closeAllDrawers} so every drawer surface closes together.
   */
  closeAll: () => void;
};

let nextKey = 0;

/** Every store created via {@link createDrawerStore} — used by {@link closeAllDrawers}. */
const drawerStoreRegistry = new Set<UseBoundStore<StoreApi<DrawerStore>>>();

/** Non-stack drawers (EligibleLegs, …) register a one-shot closer here. */
const auxiliaryDrawerClosers = new Set<() => void>();

/**
 * Register a close handler for drawers that don't use
 * {@link createDrawerStore}. Called once at module init.
 */
export function registerAuxiliaryDrawerCloser(closer: () => void): void {
  auxiliaryDrawerClosers.add(closer);
}

/**
 * Closes every stacked drawer (all entity types) plus any registered
 * auxiliary drawers. Use before cross-linking out to a full page so no
 * parent drawer (contact, order, invoice, …) stays open underneath.
 */
export function closeAllDrawers(): void {
  for (const store of drawerStoreRegistry) {
    store.getState().closeAll();
  }
  for (const closer of auxiliaryDrawerClosers) {
    closer();
  }
}

/**
 * Factory that creates a stack-based Zustand drawer store.
 *
 * Stores **IDs only** — all entity data lives in the React Query cache
 * (seeded automatically by list queries via `useInfiniteQueryAPI`).
 *
 * @example
 * ```ts
 * export const useOrderDrawerStore = createDrawerStore();
 * ```
 */
export function createDrawerStore() {
  const store = create<DrawerStore>(set => ({
    stack: [],
    isOpen: false,

    open: id => {
      set(state => ({
        stack: [...state.stack, { key: String(++nextKey), itemId: id }],
        isOpen: true,
      }));
    },

    close: () => {
      set(state => {
        const newStack = state.stack.slice(0, -1);
        return { stack: newStack, isOpen: newStack.length > 0 };
      });
    },

    closeAll: () => {
      set({ stack: [], isOpen: false });
    },
  }));

  drawerStoreRegistry.add(store);
  return store;
}
